import {
  createStyles,
  Grid,
  LoadingOverlay,
  Paper,
  Stack,
  Text
} from "@mantine/core";
import GeneralInfo from "./components/GeneralInfo";
import TimelineComponent from "./components/Timeline";
import ProjectStats from "./components/ProjectStats";
import EditableDescription from "../../components/editable/EditableDescription";
import StorefrontsCard from "./components/StorefrontsCard";
import EditableText from "../../components/editable/EditableText";
import { PROJECT_STATUS } from "../../utils/constants";
import { showNotification } from "@mantine/notifications";
import { IconX } from "@tabler/icons";
import { useLazyGetContractInfoQuery } from "../../APIs/Aws.api";
import utilities from "../../utils/utilities";
import PrivateAccessAccounts from "./components/PrivateAccessAccounts";

const INOProjectView = ({ project, form }) => {
  const { classes } = useStyles();
  const [getContractInfo] = useLazyGetContractInfoQuery();

  const handleUpdateStorefronts = async (contractId) => {
    try {
      const { data: contractInfo, error } = await getContractInfo({
        contractId
      });
      const storefrontsField = form.getInputProps("storefronts");

      if (error && error.originalStatus === 404) {
        throw new Error("Could not find the contract");
      }

      const updatedStorefronts = await Promise.allSettled(
        storefrontsField.value.map(async (storefront) => {
          if (storefront.contractId === contractId) {
            return {
              ...storefront,
              contractInfo
            };
          }

          return storefront;
        })
      ).then(utilities.filterSettledPromises);

      storefrontsField.onChange(updatedStorefronts);
    } catch (error) {
      showNotification({
        id: "update-storefronts",
        color: "red",
        title: `Failed to update ${contractId} storefront`,
        message: `${error?.error || error}. Check the console for more details`,
        icon: <IconX size={16} />,
        autoClose: 4000
      });
    }
  };

  return (
    <Grid m={0} mt={20}>
      <LoadingOverlay visible={!project} overlayBlur={2} />

      <Grid.Col md={6}>
        <Stack spacing={20}>
          <GeneralInfo
            project={project}
            name={form.getInputProps("name")}
            subTitle={form.getInputProps("subTitle")}
            author={form.getInputProps("author")}
            status={project?.status}
            logo={form.getInputProps("logo")}
            thumbnail={form.getInputProps("thumbnail")}
          />

          <Paper
            className={classes.basicCardWrapper}
            p="md"
            pr={45}
            radius="md"
            withBorder
          >
            <Text>
              Whitelist Merkle:
              <EditableText
                className={classes.basicCardText}
                {...form.getInputProps("whitelistMerkle")}
              />
            </Text>
          </Paper>

          <Paper
            className={classes.basicCardWrapper}
            p="md"
            pr={45}
            radius="md"
            withBorder
          >
            <Text>
              Sale Label:
              <EditableText
                className={classes.basicCardText}
                {...form.getInputProps("saleLabel")}
              />
            </Text>
          </Paper>

          <Paper
            className={classes.ipfsGatewayWrapper}
            p="md"
            pr={45}
            radius="md"
            withBorder
          >
            <Text>
              IPFS Gateway:
              <EditableText
                className={classes.ipfsGatewayText}
                {...form.getInputProps("ipfsGateway")}
              />
            </Text>
          </Paper>

          <Paper
            className={classes.basicCardWrapper}
            p="md"
            pr={45}
            radius="md"
            withBorder
          >
            <Text>
              Whitelist URL:
              <EditableText
                className={classes.basicCardText}
                {...form.getInputProps("whitelistUrl")}
              />
            </Text>
          </Paper>

          <Paper
            className={classes.basicCardWrapper}
            p="md"
            radius="md"
            withBorder
          >
            <PrivateAccessAccounts {...form.getInputProps("privateAccessTo")} />
          </Paper>
        </Stack>
      </Grid.Col>

      <Grid.Col md={6}>
        <Stack spacing={20}>
          <Paper p="md" radius="md" withBorder>
            <TimelineComponent
              projectType={project?.type}
              contracts={form.getInputProps("storefronts")?.value}
              timelineStages={{
                whitelistStartDate: form.getInputProps("whitelistStartDate"),
                whitelistEndDate: form.getInputProps("whitelistEndDate"),
                startDate: form.getInputProps("startDate"),
                endDate: form.getInputProps("endDate")
              }}
            />
          </Paper>

          <StorefrontsCard
            projectKey={form.getInputProps("key")}
            storefronts={form.getInputProps("storefronts")}
            canAddContract={project.status === PROJECT_STATUS.published}
            onUpdateStorefronts={handleUpdateStorefronts}
          />
        </Stack>
      </Grid.Col>

      <Grid.Col xs={12}>
        <Paper p="md" radius="md" withBorder>
          <ProjectStats
            totalRaiseUsd={form.getInputProps("totalRaiseUsd")}
            nftsOnSale={form.getInputProps("nftsOnSale")}
          />
        </Paper>
      </Grid.Col>

      <Grid.Col className={classes.descriptionWrapper} xs={12}>
        <EditableDescription
          className="description"
          projectKey={project?.key}
          description={form.getInputProps("description")}
        />
      </Grid.Col>
    </Grid>
  );
};

const useStyles = createStyles(() => {
  return {
    ipfsGatewayWrapper: {
      paddingRight: 20,

      ".edit-btn": {
        top: 0,
        right: -30,
        width: 22,
        minWidth: 22,
        height: 22,
        minHeight: 22,

        ".edit-btn-icon": {
          width: "12px",
          height: "12px"
        }
      }
    },
    ipfsGatewayText: {
      "& > div": {
        fontSize: 14
      }
    },
    basicCardWrapper: {
      paddingRight: 20,

      ".editable.mantine-Text-root": {
        wordBreak: "break-all"
      },

      ".edit-btn": {
        top: 0,
        right: -30,
        width: 22,
        minWidth: 22,
        height: 22,
        minHeight: 22,

        ".edit-btn-icon": {
          width: "12px",
          height: "12px"
        }
      }
    },
    basicCardText: {
      "& > div": {
        fontSize: 14
      }
    },
    descriptionWrapper: {
      ".editable-wrapper": {
        width: "100%"
      }
    }
  };
});

export default INOProjectView;
